import React, { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import '../styles/RocketCelebration.css';

const RocketCelebration = ({ active, onComplete, caseNumber = 'ENO-300' }) => {
    const [phase, setPhase] = useState('idle'); // 'countdown' | 'launch' | 'idle'

    useEffect(() => {
        if (!active) {
            setPhase('idle');
            return;
        }

        // Start countdown phase
        setPhase('countdown');

        // Play audio synthesis
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) {
                const ctx = new AudioCtx();
                const now = ctx.currentTime;

                // Countdown beeps (3, 2, 1)
                [0, 0.25, 0.5].forEach((t, i) => {
                    const beep = ctx.createOscillator();
                    const beepGain = ctx.createGain();
                    beep.type = 'sine';
                    beep.frequency.value = i === 2 ? 880 : 520;
                    beepGain.gain.setValueAtTime(0.12, now + t);
                    beepGain.gain.exponentialRampToValueAtTime(0.001, now + t + 0.15);
                    beep.connect(beepGain);
                    beepGain.connect(ctx.destination);
                    beep.start(now + t);
                    beep.stop(now + t + 0.15);
                });

                // Rocket thrust rumble / roar
                const bufferSize = ctx.sampleRate * 2.5;
                const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
                const data = buffer.getChannelData(0);
                for (let i = 0; i < bufferSize; i++) {
                    data[i] = Math.random() * 2 - 1;
                }
                const noise = ctx.createBufferSource();
                noise.buffer = buffer;

                const filter = ctx.createBiquadFilter();
                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(140, now + 0.7);
                filter.frequency.exponentialRampToValueAtTime(950, now + 1.9);

                const gain = ctx.createGain();
                gain.gain.setValueAtTime(0.001, now);
                gain.gain.setValueAtTime(0.08, now + 0.7);
                gain.gain.linearRampToValueAtTime(0.28, now + 1.2);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 2.5);

                noise.connect(filter);
                filter.connect(gain);
                gain.connect(ctx.destination);
                noise.start(now + 0.7);
                noise.stop(now + 2.6);

                // Fanfare melody upon reaching space
                const fanfareNotes = [
                    { freq: 523.25, time: 1.8, dur: 0.15 },
                    { freq: 659.25, time: 1.95, dur: 0.15 },
                    { freq: 783.99, time: 2.1, dur: 0.15 },
                    { freq: 1046.50, time: 2.25, dur: 0.5 }
                ];
                fanfareNotes.forEach(n => {
                    const osc = ctx.createOscillator();
                    const g = ctx.createGain();
                    osc.type = 'triangle';
                    osc.frequency.value = n.freq;
                    g.gain.setValueAtTime(0, now + n.time);
                    g.gain.linearRampToValueAtTime(0.25, now + n.time + 0.03);
                    g.gain.exponentialRampToValueAtTime(0.001, now + n.time + n.dur);
                    osc.connect(g);
                    g.connect(ctx.destination);
                    osc.start(now + n.time);
                    osc.stop(now + n.time + n.dur);
                });
            }
        } catch (e) {
            console.warn('Audio effects not supported or allowed:', e);
        }

        // Liftoff stage at 0.75s
        const tLaunch = setTimeout(() => {
            setPhase('launch');

            // Launchpad sparks and smoke
            confetti({
                particleCount: 50,
                angle: 90,
                spread: 60,
                origin: { x: 0.5, y: 0.9 },
                colors: ['#ff4500', '#ff8c00', '#ffd700', '#94a3b8'],
                scalar: 1.2
            });
        }, 750);

        // Mid-air trail burst
        const tMid = setTimeout(() => {
            confetti({
                particleCount: 75,
                angle: 90,
                spread: 75,
                origin: { x: 0.5, y: 0.5 },
                colors: ['#ff007f', '#7928ca', '#00dfd8', '#ffd700'],
                scalar: 1.25
            });
        }, 1400);

        // Sky fireworks explosion as rocket enters orbit
        const tSky = setTimeout(() => {
            confetti({
                particleCount: 220,
                spread: 160,
                origin: { x: 0.5, y: 0.15 },
                colors: ['#ffd700', '#ff3e3e', '#00e5ff', '#10b981', '#a855f7', '#ff69b4'],
                scalar: 1.4,
                ticks: 400
            });

            // Lateral celebratory bursts
            confetti({
                particleCount: 90,
                angle: 60,
                spread: 80,
                origin: { x: 0.05, y: 0.45 },
                colors: ['#ffd700', '#ff007f', '#00e5ff']
            });
            confetti({
                particleCount: 90,
                angle: 120,
                spread: 80,
                origin: { x: 0.95, y: 0.45 },
                colors: ['#ffd700', '#7928ca', '#76ff03']
            });
        }, 1900);

        // End animation after rocket has flown away
        const tEnd = setTimeout(() => {
            setPhase('idle');
            if (onComplete) onComplete();
        }, 3400);

        return () => {
            clearTimeout(tLaunch);
            clearTimeout(tMid);
            clearTimeout(tSky);
            clearTimeout(tEnd);
        };
    }, [active, onComplete]);

    if (!active && phase === 'idle') return null;

    return (
        <div className="rocket-liftoff-overlay" onClick={onComplete}>
            {phase === 'countdown' && (
                <div className="rocket-countdown-badge">
                    <span className="countdown-pill">🚀 COMPTE À REBOURS : 3... 2... 1... DÉCOLLAGE ! 🔥</span>
                </div>
            )}

            <div className={`rocket-flying-stage ${phase}`}>
                <div className="rocket-vehicle">
                    <svg viewBox="0 0 120 230" className="rocket-svg" width="95" height="180">
                        <defs>
                            <linearGradient id="rocketBodyGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#f8fafc" />
                                <stop offset="50%" stopColor="#ffffff" />
                                <stop offset="100%" stopColor="#e2e8f0" />
                            </linearGradient>
                            <linearGradient id="rocketNoseGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#ef4444" />
                                <stop offset="100%" stopColor="#b91c1c" />
                            </linearGradient>
                            <linearGradient id="rocketFinGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#38bdf8" />
                                <stop offset="100%" stopColor="#0284c7" />
                            </linearGradient>
                        </defs>
                        {/* Side Fins */}
                        <path d="M 28 128 L 6 172 L 28 158 Z" fill="url(#rocketFinGrad)" />
                        <path d="M 92 128 L 114 172 L 92 158 Z" fill="url(#rocketFinGrad)" />
                        {/* Fuselage Hull */}
                        <path d="M 28 72 Q 24 152 30 162 L 90 162 Q 96 152 92 72 Z" fill="url(#rocketBodyGrad)" stroke="#94a3b8" strokeWidth="2.5" />
                        {/* Nosecone */}
                        <path d="M 28 72 Q 60 8 92 72 Z" fill="url(#rocketNoseGrad)" />
                        {/* Center Keel Fin */}
                        <rect x="58" y="132" width="4" height="28" fill="url(#rocketFinGrad)" rx="2" />
                        {/* Porthole Outer & Inner */}
                        <circle cx="60" cy="98" r="18" fill="#38bdf8" stroke="#0284c7" strokeWidth="3" />
                        <circle cx="60" cy="98" r="14" fill="#0f172a" />
                        {/* Astronaut Tooth */}
                        <text x="60" y="104" textAnchor="middle" fontSize="16">🦷</text>
                        {/* Registration */}
                        <text x="60" y="134" textAnchor="middle" fontSize="10" fontWeight="900" fill="#0f172a" fontFamily="system-ui, -apple-system, sans-serif">{caseNumber}</text>
                        {/* Engine Nozzle */}
                        <polygon points="44,162 38,175 82,175 76,162" fill="#334155" />
                    </svg>

                    {/* Animated Thruster Flame */}
                    <div className="rocket-thruster-exhaust">
                        <div className="flame-outer"></div>
                        <div className="flame-inner"></div>
                        <div className="flame-particles">
                            <span className="p1">🔥</span>
                            <span className="p2">⚡</span>
                            <span className="p3">💥</span>
                        </div>
                    </div>

                    {/* Trailing Streamer Banner */}
                    <div className="rocket-streamer-banner">
                        <span className="streamer-text">🚩 BRAVO POUR LE 300ème CAS ! 🪐✨</span>
                    </div>
                </div>

                {/* Launchpad Smoke */}
                <div className="rocket-launchpad-smoke">
                    <span className="smoke-cloud s1">☁️</span>
                    <span className="smoke-cloud s2">💨</span>
                    <span className="smoke-cloud s3">☁️</span>
                    <span className="smoke-cloud s4">💨</span>
                </div>
            </div>
        </div>
    );
};

export default RocketCelebration;
